import React from 'react'
import Highcharts from 'highcharts'
import HighchartsAnnotations from 'highcharts/modules/annotations'
import More from 'highcharts/highcharts-more'
import HighchartsVenn from 'highcharts/modules/venn'
import HighchartsHistogram from 'highcharts/modules/histogram-bellcurve'
import HighchartsDumbbell from 'highcharts/modules/dumbbell'
import HighchartsLollipop from 'highcharts/modules/lollipop'
import styled from '@emotion/styled'
import { Colors, Fonts, printPageStyle, printStyle } from 'src/components/theme'
import { useResizeDetector } from 'react-resize-detector'
import { browserLogger } from 'src/utils/browser-logger'
import { growlError } from 'src/components/growl'
import { isInScreenshots } from 'src/utils/storybook'

// Load HighCharts Modules
if (typeof window !== 'undefined') {
  More(Highcharts)
  HighchartsVenn(Highcharts)
  HighchartsAnnotations(Highcharts)
  HighchartsHistogram(Highcharts)
  HighchartsDumbbell(Highcharts)
  HighchartsLollipop(Highcharts)
  Highcharts.AST.allowedAttributes.push('stroke-dasharray')

  // fun fact: if you don't do this hack highcharts wont work
  window.Highcharts = Highcharts

  const labels = {
    style: {
      fontSize: '9px',
      // highcharts fonts alias and throw off screenshot diff
      color: isInScreenshots() ? 'transparent' : Colors.textLight,
    },
  }

  const title = {
    style: {
      color: isInScreenshots() ? 'transparent' : Colors.textLight,
      fontWeight: Fonts.sofiaProMedium.fontWeight + '',
      fontFamily: Fonts.sofiaProMedium.fontFamily,
      fontSize: '11px',
    },
  }

  // set default font styles & spacing to apply to all charts
  // these are merged into chart options supplied at the call site
  // with callsite options taking precendence
  Highcharts.setOptions({
    chart: {
      animation: false,
      style: {
        fontFamily: Fonts.sofiaProMedium.fontFamily,
      },
    },
    xAxis: { labels, title },
    yAxis: { labels, title },
    credits: { enabled: false },
    plotOptions: {
      series: {
        animation: false,
      },
    },
  })
}

type ChartProps = {
  width: number | undefined
  height: number | undefined
  options: Highcharts.Options
}

const Box = styled.div`
  position: relative;
  ${printStyle`
    min-width: 600px !important;
  `}
  ${printPageStyle`
    position: absolute;
    left: 0;
    top: 0;
  `}
`

type ConditionalHeightProps = {
  height: number
  children?: React.ReactNode
}

// when we render certain components outside of the report builder we need to set
// an set height on them so their charts will render correctly.  Within the report builder
// everything is inside an absolutely sized flexbox and so we don't need to set a height.
const ConditionalHeight = (props: ConditionalHeightProps) => {
  const { height, children } = props
  return (
    <div
      className="conditional-height"
      style={{ position: 'relative', height: '100%', minHeight: height, width: '100%' }}>
      {children}
    </div>
  )
}

const MIN_PRINT_WIDTH = 600

export const Wrapper = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  overflow: visible;
  position: absolute;
  height: 100%;
`

type Props = {
  options: Highcharts.Options
  height?: number
}
export const ChartContainer: React.FC<Props> = (props) => {
  const { width, height, ref } = useResizeDetector()
  const { options } = props
  return (
    <ConditionalHeight height={props.height || 250}>
      <Wrapper className="wrapper" ref={ref}>
        <ContainerInner width={width} height={height} options={options}></ContainerInner>
      </Wrapper>
    </ConditionalHeight>
  )
}

export const ContainerInner: React.FC<ChartProps> = (props) => {
  const { width, height, options } = props
  const ref = React.useRef<HTMLDivElement>(null)
  const chartRef = React.useRef<Highcharts.Chart | undefined>()

  const refreshSize = () => {
    chartRef.current?.update({
      chart: {
        width: width || MIN_PRINT_WIDTH,
        height: height,
      },
    })
  }

  const createChart = () => {
    if (!ref.current) {
      return
    }
    try {
      const chart = Highcharts.chart({
        title: undefined,
        ...options,
        chart: {
          ...(options.chart ?? {}),
          renderTo: ref.current,
          reflow: false,
          // this forces a non-zero width render for printing
          width: width || MIN_PRINT_WIDTH,
          style: {
            fontWeight: '700',
          },
        },
      })
      chartRef.current = chart
    } catch (e: any) {
      browserLogger.error(e)
      growlError(e, 'Chart failed to render')
    }
  }

  React.useEffect(() => {
    createChart()
    return () => chartRef.current?.destroy()
  }, [Boolean(width && height)])

  React.useEffect(() => {
    if (!chartRef.current) {
      return
    }
    const { series, ...rest } = options
    chartRef.current.update(rest, false)
    // This manual update of the series it's to recreate the label and prevent rendering bugs
    if (series) {
      while (chartRef.current.series.length > 0) {
        chartRef.current.series[0].remove(false)
      }
      series.forEach((s) => {
        chartRef.current?.addSeries(s, false)
      })
    }
    chartRef.current.redraw()
  }, [options])

  React.useEffect(() => {
    refreshSize()
  }, [width, height])

  if (width == null || height == null) {
    return null
  }

  return <Box ref={ref} style={{ width, height }} />
}
